import React, { useState, useEffect } from "react";
import { getPlanDeCuentas, actualizarCuenta, eliminarCuenta, crearCuenta, PlanDeCuentas as PlanDeCuentasType } from "../../services/api";

interface NodoArbol {
  cuenta: PlanDeCuentasType;
  hijos: NodoArbol[];
  expandido: boolean;
}

function construirArbol(cuentas: PlanDeCuentasType[]): NodoArbol[] {
  const mapa: Record<string, NodoArbol> = {};
  const raices: NodoArbol[] = [];
  cuentas.forEach(c => { mapa[c.codContable] = { cuenta: c, hijos: [], expandido: true }; });
  cuentas.forEach(c => {
    const posiblesPadres = cuentas
      .filter(p => c.codContable.startsWith(p.codContable) && p.codContable !== c.codContable)
      .sort((a, b) => b.codContable.length - a.codContable.length);
    if (posiblesPadres.length > 0) {
      mapa[posiblesPadres[0].codContable].hijos.push(mapa[c.codContable]);
    } else {
      raices.push(mapa[c.codContable]);
    }
  });
  return raices;
}

const MONEDAS = ["<<Del documento>>", "Pesos UY", "Dólares", "Euros", "Unidades Indexadas"];

interface Props {
  empresaId: number;
  token: string;
  puedeAgregar: boolean;
  puedeEditar: boolean;
  puedeEliminar: boolean;
}

type ModoPanel = "editar" | "nueva" | null;

export default function PlanDeCuentas({ empresaId, token, puedeAgregar, puedeEditar, puedeEliminar }: Props) {
  const [arbol, setArbol] = useState<NodoArbol[]>([]);
  const [cuentas, setCuentas] = useState<PlanDeCuentasType[]>([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState<PlanDeCuentasType | null>(null);
  const [cuentaEditando, setCuentaEditando] = useState<PlanDeCuentasType | null>(null);
  const [modoPanel, setModoPanel] = useState<ModoPanel>(null);
  const [solapa, setSolapa] = useState<"basico" | "monedas" | "subanalisis">("basico");
  const [guardando, setGuardando] = useState(false);
  const [mensajeOk, setMensajeOk] = useState("");
  const [mensajeError, setMensajeError] = useState("");
  const [confirmando, setConfirmando] = useState<"eliminar" | "convertir" | null>(null);

  useEffect(() => {
    const cargar = async () => {
      const data = await getPlanDeCuentas(empresaId, token);
      setCuentas(data);
      setArbol(construirArbol(data));
      setCargando(false);
    };
    cargar();
  }, [empresaId]);

  const toggleNodo = (nodo: NodoArbol) => {
    nodo.expandido = !nodo.expandido;
    setArbol([...arbol]);
  };

  const cerrarPanel = () => {
    setModoPanel(null);
    setCuentaEditando(null);
    setCuentaSeleccionada(null);
    setMensajeOk("");
    setMensajeError("");
    setConfirmando(null);
  };

  const handleEditar = (cuenta: PlanDeCuentasType) => {
    if (!puedeEditar) return;
    setCuentaSeleccionada(cuenta);
    setCuentaEditando({ ...cuenta });
    setModoPanel("editar");
    setSolapa("basico");
    setMensajeOk("");
    setMensajeError("");
    setConfirmando(null);
  };

  const handleNuevaCuenta = () => {
    if (!cuentaSeleccionada) return;
    if (cuentaSeleccionada.imputable) {
      setConfirmando("convertir");
    } else {
      abrirFormNueva(cuentaSeleccionada);
    }
  };

  const abrirFormNueva = (padre: PlanDeCuentasType) => {
    const nuevaCuenta: PlanDeCuentasType = {
      planesDeCuentasId: 0,
      empresaId: empresaId,
      codContable: padre.codContable,
      nomRubro: "",
      titRubro: null,
      imputable: true,
      natRubro: padre.natRubro,
      txtRubro: null,
      reglaDifCam: null,
      codConsol: null,
      dhPredet: null,
      monBase: "<<Del documento>>",
      difCamPr: false,
      difCamSe: false,
      nvlRubro: (padre.nvlRubro || 0) + 1,
    };
    setCuentaEditando(nuevaCuenta);
    setModoPanel("nueva");
    setSolapa("basico");
    setMensajeOk("");
    setMensajeError("");
    setConfirmando(null);
  };

  const handleConfirmarConvertir = async () => {
    if (!cuentaSeleccionada) return;
    // Convertir el padre a agrupador
    const padreActualizado = { ...cuentaSeleccionada, imputable: false };
    await actualizarCuenta(cuentaSeleccionada.planesDeCuentasId, padreActualizado, token);
    const nuevasCuentas = cuentas.map(c =>
      c.planesDeCuentasId === cuentaSeleccionada.planesDeCuentasId ? padreActualizado : c
    );
    setCuentas(nuevasCuentas);
    setArbol(construirArbol(nuevasCuentas));
    setCuentaSeleccionada(padreActualizado);
    abrirFormNueva(padreActualizado);
  };

  const handleGuardar = async () => {
    if (!cuentaEditando) return;
    setGuardando(true);
    setMensajeOk("");
    setMensajeError("");

    if (modoPanel === "editar") {
      const ok = await actualizarCuenta(cuentaEditando.planesDeCuentasId, cuentaEditando, token);
      if (ok) {
        const nuevasCuentas = cuentas.map(c =>
          c.planesDeCuentasId === cuentaEditando.planesDeCuentasId ? cuentaEditando : c
        );
        setCuentas(nuevasCuentas);
        setArbol(construirArbol(nuevasCuentas));
        setCuentaSeleccionada(cuentaEditando);
        setMensajeOk("Guardado correctamente");
      } else {
        setMensajeError("Error al guardar");
      }
    } else if (modoPanel === "nueva") {
      const resultado = await crearCuenta(cuentaEditando, token);
      if (resultado.ok) {
        const data = await getPlanDeCuentas(empresaId, token);
        setCuentas(data);
        setArbol(construirArbol(data));
        setMensajeOk("Rubro creado correctamente");
        setTimeout(() => cerrarPanel(), 1500);
      } else {
        setMensajeError(resultado.mensaje || "Error al crear");
      }
    }
    setGuardando(false);
  };

  const handleEliminar = async () => {
    if (!cuentaSeleccionada) return;
    setConfirmando("eliminar");
  };

  const handleConfirmarEliminar = async () => {
    if (!cuentaSeleccionada) return;
    setGuardando(true);
    const resultado = await eliminarCuenta(cuentaSeleccionada.planesDeCuentasId, token);
    if (resultado.ok) {
      const data = await getPlanDeCuentas(empresaId, token);
      setCuentas(data);
      setArbol(construirArbol(data));
      cerrarPanel();
    } else {
      setMensajeError(resultado.mensaje || "No se puede eliminar");
      setConfirmando(null);
    }
    setGuardando(false);
  };

const nodoTieneMatch = (nodo: NodoArbol, busqueda: string): boolean => {
  const match =
    nodo.cuenta.codContable.toLowerCase().includes(busqueda.toLowerCase()) ||
    nodo.cuenta.nomRubro.toLowerCase().includes(busqueda.toLowerCase());
  if (match) return true;
  return nodo.hijos.some(h => nodoTieneMatch(h, busqueda));
};

const renderNodo = (nodo: NodoArbol, nivel: number) => {
  const { cuenta, hijos, expandido } = nodo;
  const tieneHijos = hijos.length > 0;
  const esAgrupador = !cuenta.imputable;
  const estaSeleccionada = cuentaSeleccionada?.planesDeCuentasId === cuenta.planesDeCuentasId;
  const puedeSeleccionar = puedeEditar || puedeAgregar;

  if (busqueda && !nodoTieneMatch(nodo, busqueda)) return null;

  return (
    <div key={cuenta.planesDeCuentasId}>
      <div
        style={{
          ...styles.fila,
          paddingLeft: `${8 + nivel * 16}px`,
          backgroundColor: estaSeleccionada ? "#e8f4fc" : nivel === 0 ? "#f4f6f8" : "#fff",
          fontWeight: esAgrupador ? 700 : 400,
          color: esAgrupador ? "#1a1a2e" : "#333",
          cursor: puedeSeleccionar ? "pointer" : tieneHijos ? "pointer" : "default",
        }}
        onClick={() => {
          if (!puedeSeleccionar) return;
          setCuentaSeleccionada(cuenta);
          if (puedeEditar) handleEditar(cuenta);
        }}
      >
        <div style={{ ...styles.col, width: 140, display: "flex", alignItems: "center", gap: 4 }}>
          <span
            style={{ width: 14, color: "#2B8FCC", fontSize: 11 }}
            onClick={e => { e.stopPropagation(); if (tieneHijos) toggleNodo(nodo); }}
          >
            {tieneHijos ? (expandido ? "▾" : "▸") : "·"}
          </span>
          <span style={{ fontFamily: "monospace", fontSize: 12, color: esAgrupador ? "#1a1a2e" : "#666" }}>
            {cuenta.codContable}
          </span>
        </div>
        <div style={{ ...styles.col, flex: 2 }}>{cuenta.nomRubro}</div>
        <div style={{ ...styles.col, flex: 2, fontSize: 11, color: "#888", fontWeight: 400 }}>{cuenta.txtRubro || ""}</div>
        <div style={{ ...styles.col, width: 100, justifyContent: "center" }}>
          {cuenta.imputable
            ? <span style={styles.badgeSi}>Imputable</span>
            : <span style={styles.badgeNo}>Agrupador</span>}
        </div>
        <div style={{ ...styles.col, width: 110, justifyContent: "center", fontFamily: "monospace", fontSize: 12, color: "#888" }}>
          {cuenta.codConsol || ""}
        </div>
        <div style={{ ...styles.col, width: 60, justifyContent: "center", fontSize: 12, color: "#666" }}>
          {cuenta.imputable ? (cuenta.natRubro === 1 ? "D" : cuenta.natRubro === 2 ? "H" : "") : ""}
        </div>
      </div>
      {tieneHijos && expandido && (
        <div>{hijos.map(hijo => renderNodo(hijo, nivel + 1))}</div>
      )}
    </div>
  );
};

  if (cargando) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Cargando plan de cuentas...</div>;

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      {/* TABLA */}
      <div style={{ ...styles.container, flex: 1, minWidth: 0 }}>
        <div style={styles.toolbar}>
          <input
            style={styles.busqueda}
            placeholder="Buscar por código o nombre..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
          {puedeAgregar && (
            <button
              style={{ ...styles.btnNuevo, opacity: cuentaSeleccionada ? 1 : 0.5 }}
              onClick={handleNuevaCuenta}
              disabled={!cuentaSeleccionada}
            >
              + Nueva cuenta
            </button>
          )}
        </div>
        <div style={styles.encabezado}>
          <div style={{ ...styles.colHeader, width: 140 }}>Código</div>
          <div style={{ ...styles.colHeader, flex: 2 }}>Nombre</div>
          <div style={{ ...styles.colHeader, flex: 2 }}>Texto</div>
          <div style={{ ...styles.colHeader, width: 100, textAlign: "center" }}>Imputable</div>
          <div style={{ ...styles.colHeader, width: 110, textAlign: "center" }}>Cód. Consol.</div>
          <div style={{ ...styles.colHeader, width: 60, textAlign: "center" }}>Nat.</div>
        </div>
        <div style={styles.tabla}>
          {arbol.map(nodo => renderNodo(nodo, 0))}
        </div>
      </div>

      {/* PANEL */}
      {modoPanel && cuentaEditando && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitulo}>
              {modoPanel === "editar" ? "Modificar Rubro" : "Nueva Cuenta"}
            </span>
            <button style={styles.panelCerrar} onClick={cerrarPanel}>✕</button>
          </div>

          <div style={styles.panelCodigo}>
            {modoPanel === "editar"
              ? `${cuentaEditando.codContable} — ${cuentaEditando.nomRubro}`
              : `Hijo de: ${cuentaSeleccionada?.codContable} — ${cuentaSeleccionada?.nomRubro}`
            }
          </div>

          {/* CONFIRMACIÓN CONVERTIR */}
          {confirmando === "convertir" && (
            <div style={styles.confirmBox}>
              <p style={styles.confirmTexto}>
                El rubro <strong>{cuentaSeleccionada?.codContable}</strong> es imputable. Al crear una cuenta hija, pasará a ser agrupador. ¿Confirmar?
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={styles.btnGuardar} onClick={handleConfirmarConvertir}>Confirmar</button>
                <button style={styles.btnCancelar} onClick={() => setConfirmando(null)}>Cancelar</button>
              </div>
            </div>
          )}

          {/* CONFIRMACIÓN ELIMINAR */}
          {confirmando === "eliminar" && (
            <div style={styles.confirmBox}>
              <p style={styles.confirmTexto}>
                ¿Eliminar el rubro <strong>{cuentaSeleccionada?.codContable} — {cuentaSeleccionada?.nomRubro}</strong>?
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={{ ...styles.btnGuardar, backgroundColor: "#e74c3c" }} onClick={handleConfirmarEliminar} disabled={guardando}>
                  {guardando ? "Eliminando..." : "Eliminar"}
                </button>
                <button style={styles.btnCancelar} onClick={() => setConfirmando(null)}>Cancelar</button>
              </div>
            </div>
          )}

          {/* FORMULARIO */}
          {!confirmando && (
            <>
              <div style={styles.solapas}>
                {(["basico", "monedas", "subanalisis"] as const).map(s => (
                  <button
                    key={s}
                    style={{ ...styles.solapa, ...(solapa === s ? styles.solapaActiva : {}) }}
                    onClick={() => setSolapa(s)}
                  >
                    {s === "basico" ? "Básico" : s === "monedas" ? "Monedas" : "Subanálisis"}
                  </button>
                ))}
              </div>

              {solapa === "basico" && (
                <div style={styles.solapaContenido}>
                  <div style={styles.campo}>
                    <label style={styles.campoLabel}>Código</label>
                    {modoPanel === "editar"
                      ? <input style={{ ...styles.campoInput, backgroundColor: "#f4f6f8", color: "#888" }} value={cuentaEditando.codContable} readOnly />
                      : <input style={styles.campoInput} value={cuentaEditando.codContable}
                          onChange={e => setCuentaEditando({ ...cuentaEditando, codContable: e.target.value })}
                          placeholder={`${cuentaSeleccionada?.codContable}XX`} />
                    }
                  </div>
                  <div style={styles.campo}>
                    <label style={styles.campoLabel}>Nombre</label>
                    <input style={styles.campoInput}
                      value={cuentaEditando.nomRubro}
                      onChange={e => setCuentaEditando({ ...cuentaEditando, nomRubro: e.target.value })} />
                  </div>
                  <div style={styles.campo}>
                    <label style={styles.campoLabel}>Naturaleza</label>
                    <select style={styles.campoInput}
                      value={cuentaEditando.natRubro ?? 0}
                      onChange={e => setCuentaEditando({ ...cuentaEditando, natRubro: Number(e.target.value) })}>
                      <option value={0}>—</option>
                      <option value={1}>D (Deudor)</option>
                      <option value={2}>H (Acreedor)</option>
                    </select>
                  </div>
                  <div style={styles.campo}>
                    <label style={styles.campoLabel}>Notas</label>
                    <textarea style={{ ...styles.campoInput, height: 70, resize: "vertical" as const }}
                      value={cuentaEditando.txtRubro || ""}
                      onChange={e => setCuentaEditando({ ...cuentaEditando, txtRubro: e.target.value })} />
                  </div>
                  <div style={styles.campo}>
                    <label style={styles.campoLabel}>Rubro para consolidación</label>
                    <input style={styles.campoInput}
                      value={cuentaEditando.codConsol || ""}
                      onChange={e => setCuentaEditando({ ...cuentaEditando, codConsol: e.target.value })} />
                  </div>
                </div>
              )}

              {solapa === "monedas" && (
                <div style={styles.solapaContenido}>
                  <div style={styles.campo}>
                    <label style={styles.campoLabel}>Moneda base</label>
                    <select style={styles.campoInput}
                      value={cuentaEditando.monBase || "<<Del documento>>"}
                      onChange={e => setCuentaEditando({ ...cuentaEditando, monBase: e.target.value })}>
                      {MONEDAS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div style={styles.campoCheck}>
                    <input type="checkbox"
                      checked={cuentaEditando.difCamPr ?? false}
                      onChange={e => setCuentaEditando({ ...cuentaEditando, difCamPr: e.target.checked })} />
                    <label style={styles.campoLabelCheck}>Genera diferencia de cambio en moneda principal</label>
                  </div>
                  <div style={styles.campoCheck}>
                    <input type="checkbox"
                      checked={cuentaEditando.difCamSe ?? false}
                      onChange={e => setCuentaEditando({ ...cuentaEditando, difCamSe: e.target.checked })} />
                    <label style={styles.campoLabelCheck}>Genera diferencia de cambio en moneda secundaria</label>
                  </div>
                </div>
              )}

              {solapa === "subanalisis" && (
                <div style={styles.solapaContenido}>
                  {[1, 2, 3, 4].map(n => (
                    <div key={n} style={styles.campo}>
                      <label style={styles.campoLabel}>Nivel {n}</label>
                      <input style={{ ...styles.campoInput, backgroundColor: "#f4f6f8", color: "#888" }}
                        value="" readOnly placeholder="— próximamente —" />
                    </div>
                  ))}
                </div>
              )}

              {mensajeOk && <div style={styles.mensajeOk}>✓ {mensajeOk}</div>}
              {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}

              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button style={styles.btnGuardar} onClick={handleGuardar} disabled={guardando}>
                  {guardando ? "Guardando..." : "Aceptar"}
                </button>
                <button style={styles.btnCancelar} onClick={cerrarPanel}>Cancelar</button>
              </div>

              {modoPanel === "editar" && puedeEliminar && (
                <button
                  style={{ ...styles.btnEliminar, marginTop: 8 }}
                  onClick={handleEliminar}
                >
                  Eliminar rubro
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" },
  toolbar: { display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #f0f0f0" },
  busqueda: { flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, outline: "none" },
  btnNuevo: { padding: "8px 16px", backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  encabezado: { display: "flex", alignItems: "center", padding: "8px 8px", backgroundColor: "#f4f6f8", borderBottom: "2px solid #e0e0e0" },
  colHeader: { fontSize: 11, fontWeight: 700, color: "#999", letterSpacing: "0.5px", textTransform: "uppercase" as const, padding: "0 8px" },
  tabla: { overflowY: "auto" as const, maxHeight: "calc(100vh - 260px)" },
  fila: { display: "flex", alignItems: "center", padding: "7px 8px", borderBottom: "1px solid #f5f5f5", fontSize: 13, transition: "background 0.1s" },
  col: { display: "flex", alignItems: "center", padding: "0 8px", overflow: "hidden", whiteSpace: "nowrap" as const, textOverflow: "ellipsis" },
  badgeSi: { backgroundColor: "#e8f4fc", color: "#2B8FCC", borderRadius: 4, fontSize: 10, padding: "2px 6px", fontWeight: 600 },
  badgeNo: { backgroundColor: "#f5f5f5", color: "#aaa", borderRadius: 4, fontSize: 10, padding: "2px 6px" },
  panel: { width: 320, backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.1)", padding: 20, flexShrink: 0 },
  panelHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  panelTitulo: { fontSize: 14, fontWeight: 700, color: "#1a1a2e" },
  panelCerrar: { background: "transparent", border: "none", cursor: "pointer", fontSize: 16, color: "#aaa" },
  panelCodigo: { fontFamily: "monospace", fontSize: 12, color: "#2B8FCC", marginBottom: 14, padding: "6px 10px", backgroundColor: "#f4f6f8", borderRadius: 6 },
  solapas: { display: "flex", borderBottom: "2px solid #e0e0e0", marginBottom: 16, gap: 0 },
  solapa: { padding: "7px 14px", border: "none", background: "transparent", fontSize: 13, cursor: "pointer", color: "#888", borderBottom: "2px solid transparent", marginBottom: -2 },
  solapaActiva: { color: "#2B8FCC", fontWeight: 600, borderBottom: "2px solid #2B8FCC" },
  solapaContenido: { minHeight: 200 },
  campo: { marginBottom: 12 },
  campoLabel: { display: "block", fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase" as const, letterSpacing: "0.5px", marginBottom: 4 },
  campoInput: { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, boxSizing: "border-box" as const, outline: "none" },
  campoCheck: { display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 },
  campoLabelCheck: { fontSize: 13, color: "#333", lineHeight: 1.4 },
  btnGuardar: { flex: 1, padding: 10, backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnCancelar: { flex: 1, padding: 10, backgroundColor: "#f4f6f8", color: "#555", border: "1px solid #ddd", borderRadius: 8, fontSize: 13, cursor: "pointer" },
  btnEliminar: { width: "100%", padding: 10, backgroundColor: "transparent", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: 8, fontSize: 13, cursor: "pointer" },
  mensajeOk: { backgroundColor: "#e8f8f0", color: "#27ae60", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 8, textAlign: "center" as const },
  mensajeError: { backgroundColor: "#fff0f0", color: "#e74c3c", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 8, textAlign: "center" as const },
  confirmBox: { backgroundColor: "#fff8e8", border: "1px solid #f0d080", borderRadius: 8, padding: 14, marginBottom: 12 },
  confirmTexto: { fontSize: 13, color: "#333", marginBottom: 12, lineHeight: 1.5 },
};